import { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useNavigate, useParams, useLocation } from 'react-router-dom';
import { useMutation } from 'convex/react';
import { api } from '../convex/_generated/api';
import type { ContentInput } from './lib/types';
import { useAuth, PERMISSIONS } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import ResetPassword from './components/ResetPassword';
import { SidebarProvider, SidebarInset } from '@/components/ui/sidebar';
import { AppSidebar } from '@/components/app-sidebar';
import { AppHeader } from '@/components/app-header';
import { CommandPalette } from '@/components/command-palette';
import Dashboard from './components/Dashboard';
import OverviewDashboard from './components/OverviewDashboard';
import ScanForm from './components/ScanForm';
import ScanHistory from './components/ScanHistory';
import MonitoringWidget from './components/MonitoringWidget';
import Settings from './components/Settings';
import UserManagement from './components/UserManagement';
import ApiMonitor from './components/ApiMonitor';
import DomainAnalytics from './components/DomainAnalytics';
import VisibilityTrends from './components/VisibilityTrends';
import ScheduledScans from './components/ScheduledScans';
import ReviewQueue from './components/ReviewQueue';
import ConfigurationCheck from './components/ConfigurationCheck';
import ErrorBoundary from './components/ErrorBoundary';
import './App.css';

function HomePage() {
  return <OverviewDashboard />;
}

function NewScanPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission } = useAuth();
  const rescanContent = (location.state as any)?.rescanContent;

  if (!hasPermission(PERMISSIONS.SCAN)) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="text-red-800 font-semibold">Access Denied</h3>
          <p className="text-red-600">You need scan permissions to start new scans.</p>
        </div>
      </div>
    );
  }

  const handleScanStarted = (batchId: string) => {
    navigate(`/scan/${batchId}`);
  };

  return (
    <ScanForm
      onScanStarted={handleScanStarted}
      initialContents={rescanContent}
    />
  );
}

function ScanResultsPage() {
  const { batchId } = useParams<{ batchId: string }>();
  const { hasPermission } = useAuth();

  if (!hasPermission(PERMISSIONS.VIEW)) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="text-red-800 font-semibold">Access Denied</h3>
          <p className="text-red-600">You need view permissions to access scan results.</p>
        </div>
      </div>
    );
  }

  if (!batchId) return null;
  return <Dashboard batchId={batchId} />;
}

function HistoryPage() {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  if (!hasPermission(PERMISSIONS.VIEW)) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="text-red-800 font-semibold">Access Denied</h3>
          <p className="text-red-600">You need view permissions to access scan history.</p>
        </div>
      </div>
    );
  }

  const handleRescan = (title: string, year?: string, type?: string, priority?: string) => {
    navigate('/new-scan', { state: { rescanContent: [{ title, year, type: type as any, priority: priority as any }] } });
  };

  const handleViewResults = (batchId: string) => {
    navigate(`/scan/${batchId}`);
  };

  return (
    <ScanHistory
      onRescan={handleRescan}
      onViewResults={handleViewResults}
    />
  );
}

function MonitoringPage() {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  if (!hasPermission(PERMISSIONS.AUTOMATION)) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="text-red-800 font-semibold">Access Denied</h3>
          <p className="text-red-600">You need automation permissions to access monitoring.</p>
        </div>
      </div>
    );
  }

  const handleRescan = (title: string, year?: string, type?: string, priority?: string) => {
    navigate('/new-scan', { state: { rescanContent: [{ title, year, type: type as any, priority: priority as any }] } });
  };

  return <MonitoringWidget onRescan={handleRescan} />;
}

function VisibilityTrendsPage() {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  if (!hasPermission(PERMISSIONS.ANALYTICS)) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="text-red-800 font-semibold">Access Denied</h3>
          <p className="text-red-600">You need analytics permissions to access search visibility.</p>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <VisibilityTrends onNavigateToScan={() => navigate('/new-scan')} />
    </ErrorBoundary>
  );
}

function DomainAnalyticsPage() {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  if (!hasPermission(PERMISSIONS.ANALYTICS)) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="text-red-800 font-semibold">Access Denied</h3>
          <p className="text-red-600">You need analytics permissions to access domain analytics.</p>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <DomainAnalytics onNavigateToScan={() => navigate('/new-scan')} />
    </ErrorBoundary>
  );
}

function ReviewQueuePage() {
  const { hasPermission } = useAuth();

  if (!hasPermission(PERMISSIONS.VIEW)) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="text-red-800 font-semibold">Access Denied</h3>
          <p className="text-red-600">You need view permissions to access the review queue.</p>
        </div>
      </div>
    );
  }

  return <ReviewQueue />;
}

function ScheduledScansPage() {
  const { hasPermission } = useAuth();

  if (!hasPermission(PERMISSIONS.AUTOMATION)) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="text-red-800 font-semibold">Access Denied</h3>
          <p className="text-red-600">You need automation permissions to access scheduled scans.</p>
        </div>
      </div>
    );
  }

  return <ScheduledScans />;
}

function SettingsPage() {
  const { hasPermission } = useAuth();

  if (!hasPermission(PERMISSIONS.ADMIN)) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="text-red-800 font-semibold">Access Denied</h3>
          <p className="text-red-600">You need admin permissions to access settings.</p>
        </div>
      </div>
    );
  }

  return <Settings />;
}

function ApiMonitorPage() {
  const { hasPermission } = useAuth();

  if (!hasPermission(PERMISSIONS.ADMIN)) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="text-red-800 font-semibold">Access Denied</h3>
          <p className="text-red-600">You need admin permissions to access API monitoring.</p>
        </div>
      </div>
    );
  }

  return <ApiMonitor />;
}

function UserManagementPage() {
  const { hasPermission } = useAuth();

  if (!hasPermission(PERMISSIONS.ADMIN)) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="text-red-800 font-semibold">Access Denied</h3>
          <p className="text-red-600">You need admin permissions to access this page.</p>
        </div>
      </div>
    );
  }

  return <UserManagement />;
}

function App() {
  const [configValidated, setConfigValidated] = useState(false);
  const [commandOpen, setCommandOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const initializeDefaults = useMutation(api.whitelist.initializeDefaults);
  const initPirateDefaults = useMutation(api.settings.initializePirateDefaults);
  const initReachabilityDefaults = useMutation(api.settings.initializeReachabilityDefaults);

  useEffect(() => {
    initializeDefaults().catch(err => {
      console.error('Failed to initialize whitelist:', err);
    });
    initPirateDefaults().catch(err => {
      console.error('Failed to initialize pirate defaults:', err);
    });
    initReachabilityDefaults().catch(err => {
      console.error('Failed to initialize reachability defaults:', err);
    });
  }, [initializeDefaults, initPirateDefaults, initReachabilityDefaults]);

  const handleLogout = async () => {
    await logout();
  };

  // Public routes that don't require authentication
  if (location.pathname === '/reset-password') {
    return (
      <Routes>
        <Route path="/reset-password" element={<ResetPassword />} />
      </Routes>
    );
  }

  return (
    <ProtectedRoute>
      <CommandPalette open={commandOpen} setOpen={setCommandOpen} />

      {!configValidated && (
        <ConfigurationCheck onValidated={() => setConfigValidated(true)} />
      )}

      <SidebarProvider>
        <AppSidebar />

        {/* SidebarInset applies the correct left inset using --sidebar-width */}
        <SidebarInset>
          <div className="flex min-h-svh w-full flex-1 flex-col">
            <AppHeader />

            <main className="min-h-[calc(100vh-8rem)] p-6">
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/login" element={<Navigate to="/" replace />} />
                <Route path="/new-scan" element={<NewScanPage />} />
                <Route path="/scan/:batchId" element={<ScanResultsPage />} />
                <Route path="/history" element={<HistoryPage />} />
                <Route path="/monitoring" element={<MonitoringPage />} />
                <Route path="/scheduled-scans" element={<ScheduledScansPage />} />
                <Route path="/review-queue" element={<ReviewQueuePage />} />
                <Route path="/visibility-trends" element={<VisibilityTrendsPage />} />
                <Route path="/domain-analytics" element={<DomainAnalyticsPage />} />
                <Route path="/api-monitor" element={<ApiMonitorPage />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/user-management" element={<UserManagementPage />} />
              </Routes>
            </main>

            <footer className="border-t py-4 px-6">
              <p className="text-sm text-muted-foreground text-center">OSN Internal Use Only</p>
            </footer>
          </div>
        </SidebarInset>
      </SidebarProvider>
    </ProtectedRoute>
  );
}

export default App;
